import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpParams
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthStateService } from './auth-state.service';

@Injectable()
export class ApiPayloadInterceptor implements HttpInterceptor {
  private readonly authStateService = inject(AuthStateService);

  private excludedUrls = [
    'identity',
    'api/CAS',
    'api/Authorization',
    'profile-image-upload',
    'resources/bulk-upload',
    "system/upload-file",
    "api/tickets/upload-attachments"
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip modification for excluded URLs
    if (this.isExcludedUrl(req.url)) {
      return next.handle(req);
    }

    const companyId = environment.COMPANY_ID;
    // Prefer the live signal, then fall back to localStorage for the current company.
    const currentCompany =
      this.authStateService.currentCompany() || localStorage.getItem('currentCompany');

    // Derive userID from the active company's persisted user details first so that
    // a company switch is reflected immediately, without waiting for the in-memory
    // signal to be refreshed.
    let userID: string | number | null = null;
    try {
      if (currentCompany) {
        const userDetails = localStorage.getItem(`tibss.${currentCompany}-userDetails`);
        if (userDetails) {
          const userObj = JSON.parse(userDetails);
          userID = userObj.userID?.toString() || null;
        }
      }
      if (!userID) {
        const signalUserId = this.authStateService.getUserId();
        userID = signalUserId ? signalUserId.toString() : null;
      }
    } catch {
      // userId = environment.USER_ID;
    }

    let modifiedReq = req;
    // Preserve headers as-is; auth headers are handled centrally by AuthInterceptor.
    const headers = req.headers;

    if (req.method === 'GET' || req.method === 'DELETE') {
      // For GET requests, add as query parameters
      let params = new HttpParams({ fromString: req.params.toString() });
      if (companyId && !params.has('companyId')) {
        params = params.append('companyId', companyId);
      }
      if (userID && !params.has('userID')) {
        params = params.append('userID', userID.toString());
      }
      modifiedReq = req.clone({
        params,
        headers,
      });
    }
    else if (req.body instanceof FormData) {
      // Do not modify FormData payloads
      modifiedReq = req.clone({ headers });
    }
    else if (['POST', 'PUT', 'PATCH'].includes(req.method) && this.isJsonContentType(req)) {
      // For POST/PUT/PATCH requests, add to body
      console.log(companyId, 'req.body', req.body);
      const newBody = {
        ...req.body,
        ...(companyId && !req.body.companyId ? { companyId } : {}),
        ...(userID && !req.body.userID ? { userID } : {}),
      };
      modifiedReq = req.clone({
        body: newBody,
        headers,
      });
    } else {
      // For other requests, just add the headers
      modifiedReq = req.clone({ headers });
    }

    return next.handle(modifiedReq);
  }

  private isJsonContentType(req: HttpRequest<any>): boolean {
    const contentType = req.headers.get('Content-Type');
    return !contentType || contentType.includes('application/json');
  }

  private isExcludedUrl(url: string): boolean {
    return this.excludedUrls.some(excludedUrl =>
      url.toLowerCase().includes(excludedUrl.toLowerCase())
    );
  }
}
